import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import SEO from "../components/SEO";
import { ORG_ID } from "../data/schema";
import { Reveal, Chapter, MaskedLine } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { PROJECTS } from "../data/content";
import { waLink, CONTEXT_MESSAGES } from "../lib/whatsapp";
import { track } from "../lib/analytics";

// Meta description between 70 and 160 characters.
const projectDescription = (project) => {
  const named = `${project.name}: ${project.objective}`;
  if (named.length > 160) return project.objective;
  if (named.length >= 70) return named;
  return `${named} A ${project.industry.toLowerCase()} website built by Grotech Web.`;
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  if (!project) return <Navigate to="/projects/" replace />;

  const blocks = [
    { label: "The Challenge", text: project.challenge },
    { label: "The Strategy", text: project.strategy },
    { label: "What Was Built", text: project.built },
  ];

  return (
    <main className="pt-36 lg:pt-44">
      <SEO
        title={`${project.name} Website Case Study`}
        description={projectDescription(project)}
        path={`/projects/${project.slug}`}
        schema={{
          "@type": "CreativeWork",
          "@id": `https://grotechweb.com/projects/${project.slug}/#project`,
          name: `${project.name} website`,
          description: project.objective,
          genre: `${project.industry} website`,
          creator: { "@id": ORG_ID },
          image: `https://grotechweb.com${project.image || "/og-image.png"}`,
        }}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Projects", path: "/projects" }, { name: project.name, path: `/projects/${project.slug}` }]}
      />
      <article className="mx-auto max-w-5xl px-4 sm:px-8" data-testid="project-detail">
        <Link
          to="/projects/"
          data-testid="project-back-link"
          className="flex w-fit items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All Projects
        </Link>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {project.sample ? (
            <span className="rounded-full bg-ember/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ember">
              Sample Case Study
            </span>
          ) : (
            <span className="flex w-fit items-center gap-1.5 rounded-full bg-ember px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
              <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live Website
            </span>
          )}
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
            {project.industry} · {project.location}
          </span>
        </div>
        <h1 className="mt-6 font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl">
          <MaskedLine>{project.name}</MaskedLine>
        </h1>
        <Reveal hero delay={0.25}>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/60">{project.objective}</p>
        </Reveal>
        <Reveal hero delay={0.3}>
          <div className="mt-10 overflow-hidden rounded-3xl border border-white/10">
            <img
              src={project.image}
              alt={`${project.name} website preview`}
              className="aspect-[16/9] w-full object-cover"
            />
          </div>
        </Reveal>

        {blocks.map((b, i) => (
          <section key={b.label} className="mt-16 grid gap-6 border-t border-white/10 pt-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Chapter index={String(i + 1).padStart(2, "0")} label={b.label} />
            </div>
            <Reveal className="lg:col-span-8">
              <p className="text-base leading-relaxed text-white/65">{b.text}</p>
            </Reveal>
          </section>
        ))}

        <section className="mt-16 grid gap-6 border-t border-white/10 pt-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Chapter index="04" label="Key Features" />
          </div>
          <div className="lg:col-span-8">
            <ul className="grid gap-3 sm:grid-cols-2">
              {project.features.map((f) => (
                <li key={f} className="flex items-start gap-3 rounded-xl border border-white/10 bg-ink p-4 text-sm text-white/70">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-ember" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-16 border-t border-white/10 pt-10">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember">Business Goal</p>
            <p className="mt-4 max-w-3xl font-display text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
              {project.goal}
            </p>
          </Reveal>
        </section>

        <Reveal>
          <div className="mt-16 mb-24 rounded-3xl bg-bone p-8 text-obsidian sm:p-12">
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
              Want a Website Built Around Your Business Goal?
            </h2>
            <p className="mt-3 max-w-lg text-sm text-obsidian/60">
              Every project starts with a free website plan — pages, journey and lead strategy mapped
              to what your business actually needs.
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <button
                data-testid="project-detail-cta"
                onClick={() => { track("cta_click", { location: "project_detail", project: slug }); openLeadPopup(); }}
                className="group flex items-center gap-2 rounded-full bg-ember px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-ember-hover"
              >
                Get a Free Website Plan <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href={waLink(CONTEXT_MESSAGES.portfolio)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="project-detail-whatsapp"
                onClick={() => track("whatsapp_click", { location: "project_detail", project: slug })}
                className="rounded-full border border-obsidian/20 px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-obsidian transition-colors hover:bg-obsidian hover:text-white"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </Reveal>
      </article>
    </main>
  );
}
